import { useMemo } from "react";
import { useGetList, useGetMany } from "react-admin";
import { DocumentLoad, ResourceName, Claimant } from "@/types";

// =============================================================================
// useDocumentLoads
// =============================================================================

interface UseDocumentLoadsResult {
  latestLoad: DocumentLoad | null;
  /** Loads other than the most recent, in reverse-chronological order. Empty
   *  when the doc has only been loaded once. Used by the previous-loads
   *  drawer to show prior load summaries. */
  previousLoads: DocumentLoad[];
  previousLoadsCount: number;
  isLoading: boolean;
}

export const useDocumentLoads = (documentId?: string): UseDocumentLoadsResult => {
  const { data, isLoading } = useGetList<DocumentLoad>(
    `documents/${documentId}/document-loads`,
    { sort: { field: "created_datetime", order: "DESC" } },
    { enabled: !!documentId }
  );

  const allLoads = data ?? [];
  const latestLoad = allLoads[0] ?? null;
  const previousLoads = allLoads.slice(1);

  return { latestLoad, previousLoads, previousLoadsCount: previousLoads.length, isLoading };
};

// =============================================================================
// useClaimantLookup
// =============================================================================

export const useClaimantLookup = (entities: Record<string, unknown>[]): Record<string, string> => {
  const claimantIds = useMemo(() => {
    const ids = new Set<string>();
    entities.forEach((entity) => {
      const claimantId = entity?.claimant_id_ref as string | undefined;
      if (claimantId) ids.add(claimantId);
    });
    return Array.from(ids);
  }, [entities]);

  const { data: claimants } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: claimantIds },
    { enabled: claimantIds.length > 0 }
  );

  return useMemo(() => {
    if (!claimants) return {};
    return claimants.reduce<Record<string, string>>((acc, c) => {
      acc[c.id] = c.claimant_name || `${c.claimant_first_name || ""} ${c.claimant_last_name || ""}`.trim() || "Unknown";
      return acc;
    }, {});
  }, [claimants]);
};
